import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './App.css';
import './AdminPage.css';

const processEmployeesForMerging = (employees) => {
    if (!employees || employees.length === 0) return [];

    const processed = employees.map((employee) => ({
        ...employee,
        showDept: false,
        showPart: false,
        showDuty: false
    }));

    for (let i = 0; i < processed.length; i++) {
        if (i === 0 || processed[i].department !== processed[i - 1].department) {
            processed[i].showDept = true;
            let rowSpan = 1;
            for (let j = i + 1; j < processed.length; j++) {
                if (processed[j].department === processed[i].department) rowSpan++;
                else break;
            }
            processed[i].deptRowSpan = rowSpan;
        }

        if (
            i === 0 ||
            processed[i].department !== processed[i - 1].department ||
            processed[i].part !== processed[i - 1].part
        ) {
            processed[i].showPart = true;
            let rowSpan = 1;
            for (let j = i + 1; j < processed.length; j++) {
                if (processed[j].department === processed[i].department && processed[j].part === processed[i].part) rowSpan++;
                else break;
            }
            processed[i].partRowSpan = rowSpan;
        }

        if (
            i === 0 ||
            processed[i].department !== processed[i - 1].department ||
            processed[i].part !== processed[i - 1].part ||
            processed[i].duty !== processed[i - 1].duty
        ) {
            processed[i].showDuty = true;
            let rowSpan = 1;
            for (let j = i + 1; j < processed.length; j++) {
                if (
                    processed[j].department === processed[i].department &&
                    processed[j].part === processed[i].part &&
                    processed[j].duty === processed[i].duty
                ) rowSpan++;
                else break;
            }
            processed[i].dutyRowSpan = rowSpan;
        }
    }

    return processed;
};

const NewEmployeeForm = ({ onAdd }) => {
    const [form, setForm] = useState({
        department: '',
        part: '',
        duty: '',
        position: '',
        name: '',
        hireDate: ''
    });

    const handleChange = (event) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!form.name || !form.hireDate) {
            alert('성명과 입사일은 필수 입력입니다.');
            return;
        }

        onAdd(form);
        setForm({ department: '', part: '', duty: '', position: '', name: '', hireDate: '' });
    };

    return (
        <form onSubmit={handleSubmit} className="employee-form">
            <input name="department" value={form.department} onChange={handleChange} placeholder="부서" />
            <input name="part" value={form.part} onChange={handleChange} placeholder="파트" />
            <input name="duty" value={form.duty} onChange={handleChange} placeholder="직무" />
            <input name="position" value={form.position} onChange={handleChange} placeholder="직급" />
            <input name="name" value={form.name} onChange={handleChange} placeholder="성명" required />
            <input name="hireDate" type="date" value={form.hireDate} onChange={handleChange} required />
            <button type="submit">직원 추가</button>
        </form>
    );
};

function EmployeeManagementPage() {
    const [employees, setEmployees] = useState([]);
    const navigate = useNavigate();
    const { logout } = useAuth();

    const fetchEmployees = () => {
        axios.get('/api/admin/employees')
            .then((response) => setEmployees(response.data))
            .catch((error) => console.error('Error fetching employees:', error));
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    const handleAddEmployee = (employeeData) => {
        axios.post('/api/admin/employee', employeeData)
            .then(() => {
                alert('직원이 추가되었습니다.');
                fetchEmployees();
            })
            .catch((error) => alert(`직원 추가 실패: ${error.response?.data?.message || '서버 오류'}`));
    };

    const handleDateChange = (employee, field, value) => {
        const payload = {
            department: employee.department,
            part: employee.part,
            duty: employee.duty,
            position: employee.position,
            name: employee.name,
            hireDate: field === 'hireDate' ? value : employee.hireDate,
            terminationDate: field === 'terminationDate' ? (value || null) : employee.terminationDate
        };

        axios.put(`/api/admin/employee/${employee.id}`, payload)
            .then(fetchEmployees)
            .catch((error) => alert(`직원 정보 수정 실패: ${error.response?.data?.message || '서버 오류'}`));
    };

    const handleDeleteEmployee = (employee) => {
        if (!window.confirm(`${employee.name} 직원을 완전히 삭제하시겠습니까? 이 직원의 기존 스케줄 데이터도 함께 삭제됩니다.`)) {
            return;
        }

        axios.delete(`/api/admin/employee/${employee.id}/permanent`)
            .then(() => {
                alert('직원이 삭제되었습니다.');
                fetchEmployees();
            })
            .catch((error) => alert(`직원 삭제 실패: ${error.response?.data?.message || '서버 오류'}`));
    };

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(employees);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        const orderedIds = items.map((item) => item.id);

        axios.put('/api/admin/employees/order', orderedIds)
            .then(() => setEmployees(items))
            .catch((error) => {
                alert(`순서 변경 실패: ${error.response?.data?.message || '서버 오류'}`);
                fetchEmployees();
            });
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const processedEmployees = processEmployeesForMerging(employees);

    return (
        <div className="App">
            <header className="App-header admin-header">
                <h1>직원관리</h1>
                <div className="admin-controls">
                    <button onClick={() => navigate('/admin')} className="secondary-button">스케줄관리</button>
                    <button onClick={handleLogout} className="logout-button">로그아웃</button>
                </div>
            </header>

            <NewEmployeeForm onAdd={handleAddEmployee} />

            <main className="grid-container">
                <DragDropContext onDragEnd={onDragEnd}>
                    <table className="schedule-grid admin-grid employee-management-grid">
                        <thead>
                            <tr>
                                <th className="info-col">부서</th>
                                <th className="info-col">파트</th>
                                <th className="info-col">직무</th>
                                <th className="info-col">직급</th>
                                <th className="info-col">성명</th>
                                <th className="date-management-col">입사일</th>
                                <th className="date-management-col">퇴사일</th>
                                <th className="employee-delete-col">삭제</th>
                            </tr>
                        </thead>

                        <Droppable droppableId="employees">
                            {(provided) => (
                                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                    {processedEmployees.map((employee, index) => (
                                        <Draggable key={employee.id} draggableId={String(employee.id)} index={index}>
                                            {(draggableProvided) => (
                                                <tr
                                                    ref={draggableProvided.innerRef}
                                                    {...draggableProvided.draggableProps}
                                                    {...draggableProvided.dragHandleProps}
                                                >
                                                    {employee.showDept && <td rowSpan={employee.deptRowSpan} className="info-col">{employee.department}</td>}
                                                    {employee.showPart && <td rowSpan={employee.partRowSpan} className="info-col">{employee.part}</td>}
                                                    {employee.showDuty && <td rowSpan={employee.dutyRowSpan} className="info-col">{employee.duty}</td>}
                                                    <td className="info-col">{employee.position}</td>
                                                    <td className="info-col">{employee.name}</td>
                                                    <td className="date-management-col">
                                                        <input
                                                            type="date"
                                                            value={employee.hireDate || ''}
                                                            onChange={(event) => handleDateChange(employee, 'hireDate', event.target.value)}
                                                        />
                                                    </td>
                                                    <td className="date-management-col">
                                                        <input
                                                            type="date"
                                                            value={employee.terminationDate || ''}
                                                            onChange={(event) => handleDateChange(employee, 'terminationDate', event.target.value)}
                                                        />
                                                    </td>
                                                    <td className="employee-delete-col">
                                                        <button type="button" className="delete-button" onClick={() => handleDeleteEmployee(employee)}>
                                                            삭제
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </tbody>
                            )}
                        </Droppable>
                    </table>
                </DragDropContext>
            </main>
        </div>
    );
}

export default EmployeeManagementPage;
